import styles from "./PlannerEventSignUp.module.scss";
import { PageMode } from "../../../common/types";
import { usePlannerEventSignUpPage } from "./hooks/usePlannerEventSignUpPage";
import { FormProvider } from "react-hook-form";
import { SingleSectionPageLayout } from "../../../common/components/common/SingleSectionPageLayout";
import { DefaultButton } from "../../../common/components/button/defaultButton/DefaultButton";
import { nameof } from "ts-simple-nameof";
import { IPlannerEventSignUpFormModel } from "./models/plannerEventSignUpFormModel";
import { RenderWhen } from "../../../common/infrastructure/components/RenderWhen";
import { DefaultToggle } from "../../../common/components/forms/inputs/toggle/DefaultToggle";
import { DefaultFormMultiSelectField } from "../../../common/components/forms/inputs/dropdown/DefaultFormMultiSelectField";
import { dateUtility } from "../../../common/infrastructure/utilities/dateUtility";
import { DefaultFormSelectField } from "../../../common/components/forms/inputs/dropdown/DefaultFormSelectField";
import { SelectField } from "../../../common/components/forms/inputs/dropdown/SelectField";
import { DefaultInputField } from "../../../common/components/forms/inputs/InputField/DefaultInputField";
import { useState } from "react";
import { UserWizardContents } from "../../administration/usersAdministration/userWizard/components/UserWizardContents";
import { AppDialog } from "../../../common/components/AppDialog/AppDialog";
import { CurrencyType } from "../../../common/enums";

const eventFilterOptions = [
  { id: 1, name: "Класове от тази повтаряща се група" },
  { id: 2, name: "Бъдещи класове със същия начален час" },
  { id: 3, name: "Всички бъдещи класове" },
];

interface IPlannerEventSignUpProps {
  pageMode: PageMode;
}

export const PlannerEventSignUp = ({ pageMode }: IPlannerEventSignUpProps) => {
  const {
    isLoading,
    event,
    paymentOptions,
    buttonText,
    pageTitle,
    form,
    users,
    relatedEvents,
    isRecurring,
    markAsArrivedAutomatically,
    enableCustomPriceInput,
    anyUsersSelected,
    hasAnyRelatedEvents,
    filterOption,
    setFilterOption,
    subscriptionPlanHiddenReason,
    onSave,
  } = usePlannerEventSignUpPage(pageMode);

  const [showNewUserDialog, setShowNewUserDialog] = useState(false);

  const onNewUserCreated = (newUserId: number) => {
    setShowNewUserDialog(false);
    const selectedUsers = form.getValues("userIds") || [];
    form.setValue("userIds", [...selectedUsers, newUserId]);
  };

  const children = (
    <div className={styles.root}>
      <p>
        Клас:{" "}
        <span>
          {event?.name}, {dateUtility.formatDateTime(event?.date)} ч.
        </span>
      </p>

      <div className={styles.root_fields}>
        <DefaultFormMultiSelectField
          label="Кои потребители искате да запишете?"
          name={nameof<IPlannerEventSignUpFormModel>((x) => x.userIds)}
          options={users ?? []}
          filterBy="name,id,phoneNumber,email"
          hasGroupedOptions
          filter
          virtualScrollerOptions={{ itemSize: 43 }}
        />
        <RenderWhen condition={hasAnyRelatedEvents}>
          <>
            <DefaultToggle
              name={nameof<IPlannerEventSignUpFormModel>((x) => x.isRecurring)}
              className={styles.root_recurring_checkbox}
              label="Искате ли да запишете за следващи подобни класове?"
            />
            <RenderWhen condition={!!isRecurring}>
              <>
                <SelectField
                  label="От кои класове искате да избирате?"
                  options={eventFilterOptions}
                  value={filterOption}
                  onChange={(value) => setFilterOption(value ?? 1)}
                  includeId={false}
                />
                <DefaultFormMultiSelectField
                  label="За кои класове искате да запишете?"
                  name={nameof<IPlannerEventSignUpFormModel>((x) => x.eventIds)}
                  options={relatedEvents}
                  includeId={false}
                  tooltip={
                    filterOption === 1
                      ? "В този списък виждате бъдещите класове от тази повтаряща се група, сортирани по дата (най-ранните са отгоре)."
                      : filterOption === 2
                        ? "В този списък виждате бъдещите класове, които започват в същия час, сортирани по дата (най-ранните са отгоре)."
                        : "В този списък виждате всички бъдещи класове за този обект, сортирани по дата (най-ранните са отгоре)."
                  }
                  required
                  filter
                  emptyMessage="Няма класове за избраната опция"
                />
              </>
            </RenderWhen>
          </>
        </RenderWhen>

        <DefaultToggle
          name={nameof<IPlannerEventSignUpFormModel>(
            (x) => x.markAsArrivedAutomatically,
          )}
          className={styles.root_recurring_checkbox}
          label="Автоматично потвърждаване на резервацията?"
        />

        <RenderWhen condition={!!markAsArrivedAutomatically}>
          <>
            <DefaultFormSelectField
              name={nameof<IPlannerEventSignUpFormModel>(
                (x) => x.paymentTypeId,
              )}
              options={paymentOptions}
              tooltip={`Опциите за метод на плащане се определят на база избраните потребители, настройките на обекта и позволените методи за събитието. </br>Ако всички избрани потребители имат абонаменти от един и същ тип, който е разрешен за събитието, ще се появи допълнителна опция като:</br>Абонаментен план (Групов).</br></br>Пример:  </br></br>Ако всички избрани потребители имат активен абонаментен план Семеен групов, и той е разрешен за това събитие, ще се появи опция:  </br>Абонаментен план (Семеен групов).${subscriptionPlanHiddenReason ? `</br></br>⚠️ ${subscriptionPlanHiddenReason}` : ""}`}
              label="Начин на плащане"
              includeId={false}
            />

            <RenderWhen condition={enableCustomPriceInput}>
              <DefaultInputField
                name={nameof<IPlannerEventSignUpFormModel>((x) => x.price)}
                currencyName={nameof<IPlannerEventSignUpFormModel>(
                  (x) => x.currencyId,
                )}
                placeholder={"20"}
                label={"Цена"}
                width={"30%"}
                type="number"
                step={0.01}
                currency={CurrencyType.EUR}
              />
            </RenderWhen>
          </>
        </RenderWhen>
      </div>

      <div className={styles.root_buttons}>
        <DefaultButton
          label={buttonText}
          type="submit"
          disabled={!anyUsersSelected}
        />
        <DefaultButton
          label={"Добави нов потребител"}
          type="button"
          design="outline"
          onClick={() => setShowNewUserDialog(true)}
        />
        <p className={styles.root_buttons_disclaimer}>
          В списъка с потребители виждате само потребителите, които не са
          записани за избрания в момента клас.
        </p>
      </div>
    </div>
  );

  const noResults = (
    <p className={styles.no_results}>
      В момента няма налични потребители, които да не са записани за този клас.
    </p>
  );

  return (
    <>
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(onSave)}>
          <SingleSectionPageLayout
            title={pageTitle}
            innerClassName={styles.layout}
            children={!isLoading && users?.length === 0 ? noResults : children}
            maxHeight={"85%"}
          />
        </form>
      </FormProvider>

      <AppDialog
        header="Добавяне на нов потребител"
        visible={showNewUserDialog}
        onHide={() => setShowNewUserDialog(false)}
        contentClassName={styles.dialog_content}
      >
        <UserWizardContents
          pageMode={"add"}
          onCreated={onNewUserCreated}
          hideFontSize
        />
      </AppDialog>
    </>
  );
};
